import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Layout from '../../wrappers/layout';

import IndicatorCard from '../dashboard/components/indicator-card';
import BuySellMetals from '../dashboard/components/buy-sell-metals';
import PageHeader from '../../shared/page-header';
import MetalTransactions from '../passbook/components/metal-transactions';
import { passbookStore } from '../../store/PassbookStore';
import PassbookService from '../../services/passbook.service';
import { Pagination } from '../../shared/common/pagination';
import Loader from '../../shared/common/loader';
import DashboardService from '../../services/dashboard.service';
import { dashboardStore } from '../../store/DashboardStore';
import Toast from '../../shared/common/toast';
import { userStore } from '../../store/UserStore';
import { formatAmount } from '../../utils';
import NomineeModal from 'pages/profile/components/NomineeModal';
import SupportButton from 'shared/common/supportButton/SupportButton';

interface PropsType {}

const metalsWithCode = [
  { code: 'Au', name: 'Gold' },
  { code: 'Ag', name: 'Silver' },
  { code: 'Pt', name: 'Platinum' },
  { code: 'Pd', name: 'Palladium' },
];

const BuyAndSell: React.FC<PropsType> = () => {
  const [metaData, setMetaData] = useState<any>({});
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [loading, setLoading] = useState<any>(false);
  const [selectedMetalData, setSelectedMetalData] = useState<any>(null);
  const [accountHolding, setAccountHolding] = useState<any>([]);

  // eslint-disable-next-line
  const [metalTransactions, setMetalTransactions] = passbookStore(
    (state: any) => [state.metalTransactions, state.setMetalTransactions]
  );
  const [
    metals,
    summary,
    customerHolding,
    setMetals,
    setSummary,
    setCustomerHolding,
  ] = dashboardStore((state: any) => [
    state.metals,
    state.summary,
    state.customerHolding,
    state.setMetals,
    state.setSummary,
    state.setCustomerHolding,
  ]);

  const [customerId, user, selectedAccount, customer] = userStore(
    (state: any) => [
      state.customerId,
      state.user,
      state.selectedAccount,
      state.customer,
    ]
  );

  useEffect((): any => {
    document.title = 'Purchase Metals - CoinBazaar Digital';
    setLoading(true);
    let res = Promise.all([
      getPassbookSummary(),
      getAllMetals(),
      getMetalTransactions(currentPage),
    ]);
    res
      .then((data) => {
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, []);

  useEffect((): any => {
    setLoading(true);
    let res = Promise.all([getMetalTransactions(currentPage)]);
    res
      .then((data) => {
        getAccountRelatedMetalValues();
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [selectedAccount]);

  /**
   * Get metal transactions
   * @param page
   */
const getMetalTransactions = async (page: number) => {
    try {
      setLoading(true);
      const res: any = await PassbookService.getMetalTransactions(
        customerId,
        page,
        selectedAccount.accountId,
        user?.userId,
        "",
        user?.partner?.id,
      );
      if (res.status === 200 && res.data.data) {
        setCurrentPage(page);
        setMetaData({
          current: res.data.meta.page,
          pages: res.data.meta.totalPages + 1,
        });

        const rawResult: any[] = res?.data?.data?.result ?? [];
        const normalized = rawResult.flatMap((entry: any) => {
          if (entry.orders && Array.isArray(entry.orders)) {
            return entry.orders.map((subOrder: any) => ({
              ...subOrder,
              mmParentOrderRef: entry.ebOrderRef,
              isGrouped: true,
            }));
          }
          return [{ ...entry, isGrouped: false }];
        });

        setMetalTransactions(normalized); 

        return Promise.resolve(true);
      } else {
        return Promise.reject(false);
      }
    } catch (e: any) {
      console.log(e.message);
      return Promise.reject(false);
    }
    finally {
      setLoading(false);
    }
  };

  /**
   * Get all metals
   */
  const getAllMetals = async () => {
    try {
      const res: any = await DashboardService.getAllMetals();
      if (res.status === 200 && res.data.data.result.length > 0) {
        setMetals(res.data.data.result);
        return Promise.resolve(true);
      } else {
        return Promise.reject(false);
      }
    } catch (e: any) {
      console.log(e.message);
      return Promise.reject(false);
    }
  };

  /**
   * Get passbook summary details
   */
  const getPassbookSummary = async () => {
    try {
      const res: any = await DashboardService.getPassbookSummary(
        customerId,
        user?.userId
      );
      if (res.status === 200 && res.data.data) {
        setSummary(res.data.data.summary);
        setAccountHolding(res.data.data.metal.accountsHolding);
        if (res.data.data.metal.customerHolding.length > 0) {
          setSelectedMetalData(
            res.data.data.metal.customerHolding.filter(
              (x: any) => x.metal === 'Au'
            ).length > 0
              ? res.data.data.metal.customerHolding.filter(
                  (x: any) => x.metal === 'Au'
                )[0]
              : null
          );
        }
        setCustomerHolding(res.data.data.metal.customerHolding);
        return Promise.resolve(true);
      } else {
        return Promise.reject(false);
      }
    } catch (e: any) {
      console.log(e.message);
      return Promise.reject(false);
    }
  };

  /**
   * Filter account related metal values
   */
  const getAccountRelatedMetalValues = () => {
    let accountHoldingData = accountHolding.filter(
      (item: any) => item.accountId === selectedAccount.accountId
    );
    if (accountHoldingData.length > 0) {
      setSelectedMetalData(
        accountHoldingData[0].accountHolding.filter(
          (x: any) => x.metal === selectedMetalData.metal
        ).length > 0
          ? accountHoldingData[0].accountHolding.filter(
              (x: any) => x.metal === selectedMetalData.metal
            )[0]
          : null
      );
      setCustomerHolding(accountHoldingData[0].accountHolding);
    }
  };

  /**
   * Set selected metal values
   * @param value
   */
  const setSelectedMetalValues = (value) => {
    setSelectedMetalData(
      customerHolding.filter((x: any) => x.metal === value).length > 0
        ? customerHolding.filter((x: any) => x.metal === value)[0]
        : null
    );
  };

  return (
    <Layout>
      {loading ? (
        <Loader />
      ) : (
        <>
          <PageHeader
            mainTitle='Buy and Sell'
            subTitle='Below are your transaction details'
          />
          {/* {selectedMetalData && ( */}
          <div>
            {/*<Toast/>*/}

            <Row>
              <Col md={8}>
                <Row>
                  <Col md={6}>
                    <div className='my-1 col col-sm-12 '>
                      <IndicatorCard
                        isMain
                        heading='Metal Holdings'
                        amount={`₹ ${formatAmount(summary.metalBalance)}`}
                        backgroundColor='#3A3733'
                        addClassName='tot-card'
                        styles=''
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className='my-1 col col-sm-12 '>
                      {selectedMetalData && (
                        <IndicatorCard
                          // eslint-disable-next-line react/no-array-index-key
                          isMain={false}
                          heading={
                            metalsWithCode.filter(
                              (x: any) => x.code === selectedMetalData.metal
                            ).length > 0
                              ? metalsWithCode.filter(
                                  (x: any) => x.code === selectedMetalData.metal
                                )[0].name
                              : ''
                          }
                          amount={`₹ ${
                            formatAmount(selectedMetalData.value) || ' 0.00'
                          }`}
                          quantity={`${selectedMetalData.quantity.toFixed(4)}`}
                          addClassName={`${selectedMetalData.metal} metal`}
                          earmarkedBalance={
                            selectedMetalData.lockQuantity || ''
                          }
                        />
                      )}
                    </div>
                  </Col>
                </Row>
                <Row className='mt-3'>
                  <Col md={12}>
                    <h5>Past Buy & Sell Transactions</h5>
                    <div className='t-wrap overflow-auto'>
                       <MetalTransactions metalData={metalTransactions} type="Past Buy & Sell Transactions" />
                    </div>
                    <Row>
                      <Col md={12}>
                        {/* Pagination Bar */}
                        {!loading && metaData.pages > 1 && (
                          <Pagination
                            meta={metaData}
                            handlePageChange={(number: number) =>
                              getMetalTransactions(number)
                            }
                          />
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col md={4} className='py-2'>
                {selectedMetalData && (
                  <BuySellMetals
                    metals={metals}
                    walletBalance={summary.walletBalance}
                    isDashboard={false}
                    setSelectedMetalData={setSelectedMetalValues}
                    getPassbookSummary={getPassbookSummary}
                    selectedMetalData={selectedMetalData}
                    getMetalTransactions={getMetalTransactions}
                  />
                )}
              </Col>
            </Row>

            {/*<Row>*/}
            {/*<Col md={8}>*/}
            {/*  <div className="grid">*/}
            {/*    <div>*/}
            {/*      <IndicatorCard*/}
            {/*          isMain*/}
            {/*          heading="Total Holdings"*/}
            {/*          amount={`₹ ${formatAmount(summary.walletBalance + summary.metalBalance)}`}*/}
            {/*          backgroundColor="#301250"*/}
            {/*          addClassName="tot-card"*/}
            {/*          styles=""*/}
            {/*      />*/}
            {/*    </div>*/}
            {/*    <div>*/}
            {/*      {*/}
            {/*          selectedMetalData &&*/}
            {/*          <IndicatorCard*/}
            {/*              // eslint-disable-next-line react/no-array-index-key*/}
            {/*              isMain={false}*/}
            {/*              heading={metalsWithCode.filter((x: any) => x.code === selectedMetalData.metal).length > 0*/}
            {/*                  ? metalsWithCode.filter((x: any) => x.code === selectedMetalData.metal)[0].name : ''}*/}
            {/*              amount={`₹ ${formatAmount(selectedMetalData.value) || ' 0.00'}`}*/}
            {/*              quantity={`${selectedMetalData.quantity.toFixed(4)} g`}*/}
            {/*              addClassName={`${selectedMetalData.metal} metal`}*/}
            {/*              earmarkedBalance={selectedMetalData.lockQuantity || ''}*/}
            {/*          />*/}
            {/*      }*/}
            {/*    </div>*/}

            {/*  </div>*/}

            {/*</Col>*/}

            {/*</Row>*/}
            {/* <SupportButton /> */}
          </div>
          {/* )} */}
          {/* {customer.customerCategory === 'UNREGISTERED' &&
            customer?.nomineeDetails?.length < 1 && <NomineeModal />} */}
        </>
      )}
    </Layout>
  );
};

export default BuyAndSell;
